import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, MenuItem, Select, Stack, TextField, Typography, useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import Popup from "../../../components/Popup";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";
import AxionControls from "../../../components/controls/AxionControls";
import { HideIfNoRole } from "../../../auth/authUi";
import { CMMS_MANAGER_ROLES } from "../../cmms/cmmsRoles";
import {
  createWorkOrder,
  getWorkOrderById,
  updateWorkOrder,
} from "../../../fetchers/cmmsWorkOrder";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { flattenAssetNodes } from "../../cmms/assetHierarchyUtils";
import {
  getAssetHierarchyPalette,
  getAxionFieldAlignSx,
  getContainedButtonSx,
  getMenuSx,
  getOutlinedButtonSx,
  getSelectSx,
} from "../../cmms/assetHierarchyStyles";
import { fetchAssetHierarchyTree } from "../../../fetchers/cmmsAssetHierarchy";
import { fetchUsers } from "../../../fetchers/user";
import { getTrades } from "../../../fetchers/cmmsTrade";
import { toDayjsLocale } from "../../cmms/cmmsDateLocale";
import { asUtcIso } from "../../cmms/cmmsFormat";
import {
  formatEstimatedHoursInput,
  parseEstimatedHours,
  validateEstimatedHours,
} from "../../cmms/cmmsEstimatedHours";
import { formatCmmsError } from "../../cmms/components/CmmsStates";
import {
  findTrade,
  getPrefillTradeId,
  isTradeMember,
  toTradeAssigneeOptions,
  toTradeOptions,
} from "../../cmms/cmmsTradeAssignment";
import {
  WorkOrderPriority,
  WorkOrderType,
  getWorkOrderPriorityOptions,
  getWorkOrderTypeOptions,
} from "../workOrderEnums";

const emptyForm = {
  title: "",
  description: "",
  assetNodeId: null,
  workOrderType: WorkOrderType.MachineBreakdown,
  priority: WorkOrderPriority.Medium,
  assignedDepartmentId: null,
  tradeId: null,
  assignedResourceId: null,
  plannedStartDate: null,
  plannedEndDate: null,
  estimatedHours: "",
  failureCode: "",
  failureCauseNote: "",
};

// asUtcIso on the way in: the API sends UTC without a trailing Z, so a bare dayjs(value) would
// read it as local and show the picker a time two hours behind what was saved.
const toDayjs = (value) => (value ? dayjs(asUtcIso(value)) : null);

// ...and on the way out too, for the same reason. The load effect seeds form state with the raw
// API string, and onChange only replaces it with a Dayjs if the user actually touches the picker.
// Saving an untouched field would otherwise re-read that naive string as local and write the
// instant back shifted by the local offset — every edit-save drifting it one offset further.
const toIsoOrNull = (value) => {
  if (!value) return null;
  const d = dayjs(asUtcIso(value));
  return d.isValid() ? d.toISOString() : null;
};

const buildOption = (id, title) => (id ? { id, title } : null);

export default function WorkOrderFormDialog({ open, onClose, workOrderId, companyKey, onSuccess }) {
  const intl = useIntl();
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const dateLocale = toDayjsLocale(intl.locale);
  const isEdit = Boolean(workOrderId);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const treeQuery = useQuery(
    ["cmmsAssetHierarchyTree", companyKey, false],
    () => fetchAssetHierarchyTree(companyKey, false),
    { enabled: open && Boolean(companyKey), staleTime: 300000 },
  );

  const usersQuery = useQuery(["users"], () => fetchUsers(), { enabled: open, staleTime: 300000 });

  /*
   * Retired trades included: an order already on one has to be able to show it, and
   * toTradeOptions keeps them out of the picker otherwise. The settings tab's key with its switch
   * on, so the two share a cache entry and that tab's invalidation of ["cmmsTrades"] reaches here.
   */
  const tradesQuery = useQuery(
    ["cmmsTrades", companyKey, true],
    () => getTrades(companyKey, true),
    { enabled: open && Boolean(companyKey), staleTime: 30000 },
  );

  const workOrderQuery = useQuery(
    ["cmmsWorkOrder", companyKey, workOrderId],
    () => getWorkOrderById(companyKey, workOrderId),
    { enabled: open && isEdit && Boolean(companyKey), staleTime: 0 },
  );

  const flatNodes = useMemo(() => flattenAssetNodes(treeQuery.data), [treeQuery.data]);

  const assetOptions = useMemo(
    () => flatNodes.map((node) => ({ id: node.id, title: node.name })),
    [flatNodes],
  );

  const departmentOptions = useMemo(
    () => flatNodes.filter((node) => node.nodeType === 2).map((node) => ({ id: node.id, title: node.name })),
    [flatNodes],
  );

  const trades = tradesQuery.data;
  const selectedTrade = useMemo(() => findTrade(trades, form.tradeId), [trades, form.tradeId]);

  const tradeOptions = useMemo(
    () => toTradeOptions(trades, form.tradeId, (name) => intl.formatMessage(messages.fieldTradeRetired, { name })),
    [trades, form.tradeId, intl],
  );

  /*
   * Trade first, then a person: with a trade picked, only its members. The server refuses anyone
   * else with CMMS_ASSIGNEE_NOT_IN_TRADE, so offering them would only set up a failed save.
   */
  const userOptions = useMemo(
    () => toTradeAssigneeOptions(usersQuery.data, selectedTrade, form.assignedResourceId),
    [usersQuery.data, selectedTrade, form.assignedResourceId],
  );

  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (isEdit) {
      if (workOrderQuery.data) {
        const wo = workOrderQuery.data;
        setForm({
          title: wo.title ?? "",
          description: wo.description ?? "",
          assetNodeId: wo.assetNodeId ?? null,
          workOrderType: wo.workOrderType,
          priority: wo.priority,
          assignedDepartmentId: wo.assignedDepartmentId ?? null,
          tradeId: wo.tradeId ?? null,
          assignedResourceId: wo.assignedResourceId ?? null,
          plannedStartDate: wo.plannedStartDate ?? null,
          plannedEndDate: wo.plannedEndDate ?? null,
          // The number as saved; formatEstimatedHoursInput puts it in the reader's notation.
          estimatedHours: wo.estimatedHours ?? "",
          failureCode: wo.failureCode ?? "",
          failureCauseNote: wo.failureCauseNote ?? "",
        });
      }
    } else {
      setForm(emptyForm);
    }
  }, [open, isEdit, workOrderQuery.data]);

  const handleField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  /*
   * A new department brings its trade along when exactly one serves it: what the server would
   * derive on create, put where the manager can see it before saving. getPrefillTradeId says when
   * it holds back. Only on a change made here, never when an edit loads, so opening an order does
   * not quietly rewrite the trade it was saved with.
   */
  const handleDepartmentChange = (departmentId) => {
    setForm((current) => {
      const prefill = getPrefillTradeId(departmentId, current.assignedResourceId, trades, flatNodes);
      return {
        ...current,
        assignedDepartmentId: departmentId,
        ...(prefill != null ? { tradeId: prefill } : {}),
      };
    });
  };

  /*
   * A person who is not in the new trade is taken off rather than kept. The server checks
   * membership whenever the trade changes, so keeping them would turn Save into a 400. Clearing
   * the trade keeps the person: without one, any technician will do.
   */
  const handleTradeChange = (tradeId) => {
    setForm((current) => {
      const trade = findTrade(trades, tradeId);
      const keepsAssignee = !trade || current.assignedResourceId == null
        || isTradeMember(trade, current.assignedResourceId);
      return {
        ...current,
        tradeId,
        ...(keepsAssignee ? {} : { assignedResourceId: null }),
      };
    });
  };

  const handleTextChange = (event) => {
    handleField(event.target.name || event.target.id, event.target.value);
  };

  const validate = () => {
    const next = {};
    if (!form.title?.trim()) next.title = intl.formatMessage(messages.fieldTitleRequiredError);
    if (!form.assetNodeId) next.assetNodeId = "Asset is required";
    const estimatedHoursError = validateEstimatedHours(form.estimatedHours, intl);
    if (estimatedHoursError) next.estimatedHours = estimatedHoursError;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const findOption = (options, id) => options.find((option) => option.id === id) ?? null;

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        companyKey,
        assetNodeId: form.assetNodeId,
        title: form.title.trim(),
        description: form.description?.trim() || null,
        workOrderType: form.workOrderType,
        priority: form.priority,
        assignedDepartmentId: form.assignedDepartmentId || null,
        // Always sent: the server saves an update's trade as it arrives, so leaving it out would
        // take the trade off every order edited here.
        tradeId: form.tradeId || null,
        assignedResourceId: form.assignedResourceId || null,
        plannedStartDate: toIsoOrNull(form.plannedStartDate),
        plannedEndDate: toIsoOrNull(form.plannedEndDate),
        // Always sent, as the trade is: an update saves it as it arrives, so leaving it out would
        // wipe the estimate off every order edited here. null is an empty box, not zero.
        estimatedHours: parseEstimatedHours(form.estimatedHours),
        failureCode: form.failureCode?.trim() || null,
        failureCauseNote: form.failureCauseNote?.trim() || null,
      };
      let result;
      if (isEdit) {
        result = await updateWorkOrder(workOrderId, payload);
      } else {
        // No createdByUserId: the API stamps the reporter from the token and ignores anything
        // the body says about it.
        result = await createWorkOrder(payload);
      }
      pushNotification(isEdit ? "Work order updated" : "Work order created");
      onSuccess?.(result);
      onClose?.();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, "Could not save work order"));
    } finally {
      setSubmitting(false);
    }
  };

  const assetValue = findOption(assetOptions, form.assetNodeId);
  const departmentValue = findOption(departmentOptions, form.assignedDepartmentId);
  const tradeValue = findOption(tradeOptions, form.tradeId);
  const resourceValue = findOption(userOptions, form.assignedResourceId);

  return (
    <Popup
      openPopup={open}
      setOpenPopup={(value) => { if (!value) onClose?.(); }}
      maxWidth="md"
      title={intl.formatMessage(isEdit ? messages.editWorkOrderTitle : messages.addWorkOrderTitle)}
    >
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          {isEdit ? "Update work order metadata." : "Create a new work order for a maintenance asset."}
        </Typography>

        {/*
          * A plain MUI TextField, like EscalateDialog's reason. AxionTextBox accepts an `error`
          * prop and renders it nowhere, so a blank title made Save do nothing with no word at the
          * field about why. It is also uncontrolled, so it kept whatever the form held when the
          * dialog opened rather than the title the edit effect loads afterwards.
          */}
        <TextField
          label={intl.formatMessage(messages.fieldTitle)}
          required
          value={form.title}
          onChange={(event) => handleField("title", event.target.value)}
          error={Boolean(errors.title)}
          helperText={errors.title}
          size="small"
          fullWidth
        />

        <AxionControls.AxionMultiLineText
          name="description"
          label={intl.formatMessage(messages.fieldDescription)}
          value={form.description}
          rows={3}
          onChange={handleTextChange}
        />

        {/*
          * Four kinds of field share this stack — Axion text boxes, Axion autocompletes, MUI
          * selects under a caption and two date pickers — and only the text boxes reserve the
          * 11px their floating label needs. The other three carry it from the helper, so the
          * dialog steps evenly instead of alternating 16 and 27.
          *
          * On an autocomplete the sx lands on the Autocomplete rather than on the FormControl
          * around it — the control spreads it there, unlike AxionSelect. Same result: the
          * Autocomplete is the FormControl's first child.
          */}
        <AxionControls.AxionAutocomplete
          name="assetNodeId"
          label={intl.formatMessage(messages.fieldAssetRequired)}
          options={assetOptions}
          value={assetValue}
          onChange={(_event, option) => handleField("assetNodeId", option?.id ?? null)}
          error={errors.assetNodeId}
          sx={getAxionFieldAlignSx()}
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={getAxionFieldAlignSx()}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}><FormattedMessage {...messages.fieldType} /></Typography>
            <Select
              fullWidth
              size="small"
              value={form.workOrderType}
              onChange={(event) => handleField("workOrderType", event.target.value)}
              sx={getSelectSx(cmmsColors)}
              MenuProps={{ PaperProps: { sx: getMenuSx(cmmsColors) } }}
            >
              {getWorkOrderTypeOptions(intl).map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </Box>
          <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}><FormattedMessage {...messages.fieldPriority} /></Typography>
              <Select
                fullWidth
                size="small"
                value={form.priority}
                onChange={(event) => handleField("priority", event.target.value)}
                sx={getSelectSx(cmmsColors)}
                MenuProps={{ PaperProps: { sx: getMenuSx(cmmsColors) } }}
              >
                {getWorkOrderPriorityOptions(intl).map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </Box>
          </HideIfNoRole>
        </Stack>

        {/*
          * Where, then who: the department is the area the machine stands in, the trade is the
          * crew that gets the work. Side by side because picking the first fills in the second.
          */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <AxionControls.AxionAutocomplete
              name="assignedDepartmentId"
              label={intl.formatMessage(messages.fieldAssignedDepartment)}
              options={departmentOptions}
              value={departmentValue}
              onChange={(_event, option) => handleDepartmentChange(option?.id ?? null)}
              sx={getAxionFieldAlignSx()}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <AxionControls.AxionAutocomplete
              name="tradeId"
              label={intl.formatMessage(messages.fieldTrade)}
              options={tradeOptions}
              value={tradeValue}
              onChange={(_event, option) => handleTradeChange(option?.id ?? null)}
              sx={getAxionFieldAlignSx()}
            />
          </Box>
        </Stack>

        <AxionControls.AxionAutocomplete
          name="assignedResourceId"
          label={intl.formatMessage(messages.fieldAssignedResource)}
          options={userOptions}
          value={resourceValue}
          onChange={(_event, option) => handleField("assignedResourceId", option?.id ?? null)}
          sx={getAxionFieldAlignSx()}
        />

        {/*
          * The plan in one row: when, and for how long. The estimate is what the week overview
          * adds up per trade. A text box rather than type="number": how that one takes a decimal
          * comma depends on the browser and its locale, and what it cannot parse it reports as
          * empty, which would save as "no estimate". inputMode still brings up the number pad on
          * a tablet.
          */}
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dateLocale}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={getAxionFieldAlignSx()}>
            <DateTimePicker
              label={intl.formatMessage(messages.fieldPlannedStart)}
              value={toDayjs(form.plannedStartDate)}
              onChange={(value) => handleField("plannedStartDate", value)}
              renderInput={(params) => <TextField fullWidth size="small" {...params} />}
            />
            <DateTimePicker
              label={intl.formatMessage(messages.fieldPlannedEnd)}
              value={toDayjs(form.plannedEndDate)}
              onChange={(value) => handleField("plannedEndDate", value)}
              renderInput={(params) => <TextField fullWidth size="small" {...params} />}
            />
            <TextField
              label={intl.formatMessage(cmmsMessages.estimatedHoursLabel)}
              value={formatEstimatedHoursInput(intl, form.estimatedHours)}
              onChange={(event) => handleField("estimatedHours", event.target.value)}
              error={Boolean(errors.estimatedHours)}
              helperText={errors.estimatedHours}
              inputProps={{ inputMode: "decimal" }}
              size="small"
              sx={{ flex: { sm: "0 0 180px" } }}
            />
          </Stack>
        </LocalizationProvider>

        <AxionControls.AxionTextBox
          name="failureCode"
          label={intl.formatMessage(messages.fieldFailureCode)}
          value={form.failureCode}
          onChange={handleTextChange}
        />

        <AxionControls.AxionMultiLineText
          name="failureCauseNote"
          label={intl.formatMessage(messages.fieldFailureCauseNote)}
          value={form.failureCauseNote}
          rows={3}
          onChange={handleTextChange}
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting} sx={getOutlinedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.cancel} />
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={getContainedButtonSx(cmmsColors)}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </Stack>
      </Box>
      <AxionControls.AxionBackdrop open={submitting} />
    </Popup>
  );
}
